import { useEffect, useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const DEFAULTS = {
  logo_url: '/ntoa-logo.png',
  primary_color: '#1e3a8a',
  accent_color: '#f59e0b',
  hero_gradient_from: '#0f172a',
  hero_gradient_to: '#b45309',
  tagline: 'National Transportation Operators Association',
};

/**
 * useNTOABranding — fetches the singleton branding doc with a
 * stale-while-revalidate strategy:
 *
 *   1. Render synchronously with the last-known value (from `localStorage`
 *      if a previous tab cached one, else hardcoded DEFAULTS) so the page
 *      never flashes unstyled.
 *   2. Always refetch from the API on mount — guarantees that any SA
 *      branding change is visible after the portal user's next navigation
 *      (no full-reload required).
 *   3. Persist the latest payload back to `localStorage` so the next mount
 *      / next page reload still gets an instant render.
 *
 * A module-level subscriber set means every mounted component refreshes
 * together when ANY mount finishes its fetch — so flipping between tabs
 * inside the same session reflects the freshest server value once any
 * tab has fetched it.
 */
const LS_KEY = 'ntoa_branding_v1';
const subscribers = new Set();
let _latest = null;

function readCache() {
  if (_latest) return _latest;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      _latest = { ...DEFAULTS, ...JSON.parse(raw) };
      return _latest;
    }
  } catch { /* localStorage may be disabled */ }
  return DEFAULTS;
}

function broadcast(next) {
  _latest = next;
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
  subscribers.forEach((fn) => {
    try { fn(next); } catch { /* swallow */ }
  });
}

async function refresh() {
  try {
    const res = await fetch(`${API}/insured/ntoa/branding`, { credentials: 'include' });
    if (!res.ok) return;
    const data = await res.json();
    const next = { ...DEFAULTS, ...(data?.branding || {}) };
    broadcast(next);
  } catch { /* offline or unauthenticated — keep prior value */ }
}

export default function useNTOABranding() {
  const [branding, setBranding] = useState(readCache);

  useEffect(() => {
    // Subscribe so admin saves or other tab updates flow through live.
    subscribers.add(setBranding);
    // Always trigger a refetch on mount — stale-while-revalidate.
    refresh();
    return () => subscribers.delete(setBranding);
  }, []);

  return branding;
}

/** Called by the admin BrandingPanel after a successful PUT so portal
 *  components mounted in the same tab pick up new colors instantly. */
export function notifyBrandingChanged() {
  refresh();
}

export function resolveLogoUrl(logo_url) {
  if (!logo_url) return '/ntoa-logo.png';
  if (logo_url.startsWith('http')) return logo_url;
  if (logo_url.startsWith('/ntoa-logo')) return logo_url;
  return `${process.env.REACT_APP_BACKEND_URL || ''}${logo_url}`;
}
